import { GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';

import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { BankIcon } from '@/components/icons/BankIcon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { DataTableCard } from '@/components/tables/DataTable/DataTableCard';
import { useModalState } from '@/hooks/useModalState';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { AIOnboardingSuggestionDrawer } from '@/modules/aiSuggestions/components/AIOnboardingSuggestionDrawer';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ROUTE_KEYS } from '@/navigation/constants';
import { COLORS } from '@/styles/tokens/colors';

import { EstateCreateButton } from '../components/EstateCreateButton';
import { useEstateHoldingsContext } from '../contexts/estateHoldings.context';
import { EstateEntitiesTableRowData } from './EstateEntitiesTable.constants';
import { mapEntitiesToRows, useColumns } from './EstateEntitiesTable.utils';

export function EstateEntitiesTable() {
  const { entities, loading } = useEstateHoldingsContext();
  const householdId = useRequiredParam('householdId');
  const canEdit = useCanEditHouseholdData();
  const { navigate } = useNavigateToRoute();
  const [suggestionDrawerState, suggestionDrawerModal] = useModalState<{
    suggestionId: string;
  }>();
  const handleLocateSuggestion = useCallback(
    (suggestionId: string) => {
      suggestionDrawerModal.openModal({ suggestionId });
    },
    [suggestionDrawerModal]
  );
  const columns = useColumns(handleLocateSuggestion);

  const rows = useMemo(() => mapEntitiesToRows(entities ?? []), [entities]);

  if (!loading && rows.length === 0) {
    return (
      <SmallTableEmptyState
        fill="viewport"
        icon={<BankIcon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="No entities"
        description="There are no entities associated with this client"
        action={
          canEdit ? <EstateCreateButton householdId={householdId} /> : undefined
        }
      />
    );
  }

  return (
    <>
      <DataTableCard>
        <DataTable
          loading={loading}
          rows={rows}
          columns={columns}
          pageSize={50}
          sortingMode="client"
          sortingOrder={['asc', 'desc']}
          initialState={{
            sorting: {
              sortModel: [{ field: 'currentValue', sort: 'desc' }],
            },
          }}
          onRowClick={({ row }: GridRowParams<EstateEntitiesTableRowData>) => {
            navigate(ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS, {
              householdId,
              entityId: row.entityId,
            });
          }}
        />
      </DataTableCard>
      {suggestionDrawerState.isModalOpen && suggestionDrawerState.data && (
        <AIOnboardingSuggestionDrawer
          isOpen={suggestionDrawerState.isModalOpen}
          onClose={() => suggestionDrawerModal.closeModal()}
          suggestionId={suggestionDrawerState.data.suggestionId}
        />
      )}
    </>
  );
}
